import { Dialog, DialogContent, DialogHeader, DialogTitle } from "frontend-components";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { taskListWithTasksOptions } from "@dashboard/api/queryApi";
import type { TaskListWithTaskOccurrences } from "@dashboard/api/task";

import { ConfirmDiscardDialog } from "@dashboard/components/common/ConfirmDiscardDialog";

import { ListFormBody, type ListFormBodyHandle } from "./ListFormBody";
import { ListModalStatusView } from "./ListModalLoadedView";

export function ListModalCopyView({
    sourceId,
    onClose,
}: {
    sourceId: number;
    onClose: () => void;
}) {
    const { data: source, isLoading, error } = useQuery(taskListWithTasksOptions(sourceId));

    if (isLoading || error || !source || !source.id) {
        return (
            <ListModalStatusView
                isLoading={isLoading}
                errorMessage={error?.message}
                listId={sourceId}
                onClose={onClose}
            />
        );
    }

    return <ListModalCreateView source={source} onClose={onClose} />;
}

export function ListModalCreateView({
    source,
    template = false,
    onClose,
}: {
    source?: TaskListWithTaskOccurrences;
    template?: boolean;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const formRef = useRef<ListFormBodyHandle | null>(null);
    const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

    const isFormDirty = () => formRef.current?.isDirty() ?? false;

    const requestClose = () => {
        if (isFormDirty()) {
            setConfirmDiscardOpen(true);
            return;
        }
        onClose();
    };

    return (
        <>
            <Dialog
                open
                onOpenChange={(open) => {
                    if (!open) requestClose();
                }}
            >
                <DialogContent className="bg-compendia-card max-h-[90vh] overflow-y-auto overflow-x-hidden md:overflow-hidden flex flex-col sm:max-w-[calc(100%-2rem)] md:w-[calc(100vw-4rem)] md:max-w-[1100px] text-compendia-foreground gap-0">
                    <DialogHeader className="-mx-6 pl-6 pr-24 pb-4 border-b border-compendia-border">
                        <DialogTitle className="text-lg font-bold text-compendia-foreground leading-tight overflow-hidden text-ellipsis whitespace-nowrap">
                            {t(template ? "list.newTemplateTitle" : "list.newListTitle")}
                        </DialogTitle>
                    </DialogHeader>
                    <ListFormBody
                        key="new"
                        ref={formRef}
                        list={null}
                        source={source}
                        template={template}
                        onCancel={requestClose}
                        onSaved={() => onClose()}
                    />
                </DialogContent>
            </Dialog>

            <ConfirmDiscardDialog
                open={confirmDiscardOpen}
                onOpenChange={setConfirmDiscardOpen}
                onConfirm={() => {
                    setConfirmDiscardOpen(false);
                    onClose();
                }}
            />
        </>
    );
}
